import * as React from 'react';
import styled, { css } from 'styled-components';
import getColour from '@lendi-ui/commons/colours';
import getTypography from '@lendi-ui/commons/typography';
import { getRadius } from '@lendi-ui/commons/borders';
import { normalise, deriveSize } from '@lendi-ui/utils';
import { map, gte } from '@lendi-ui/breakpoint';
import { Button } from '@lendi-ui/button';
import { jsx, jsxs } from 'react/jsx-runtime';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _taggedTemplateLiteral(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

var InputWithButton = props => {
  var InputWithButton = styled(Button).withConfig({
    displayName: "InputButton__InputWithButton",
    componentId: "lui__sc-1t83oga-0"
  })(["", ";"], props => {
    if (props.variant === 'secondary') {
      return css(["border:1px solid ", ";"], getColour('border', 'interaction'));
    }

    return undefined;
  });
  return /*#__PURE__*/jsx(InputWithButton, _objectSpread2({}, props));
};

var InputButton = styled(InputWithButton).withConfig({
  displayName: "InputButton",
  componentId: "lui__sc-1t83oga-1"
})([":focus{outline:5px auto -webkit-focus-ring-color;opacity:1;box-shadow:none;transform:scale(1);}:active{transform:scale(1);}"]);

var _templateObject, _templateObject2;

var heightBySizeMixin = size => map(size, val => {
  switch (val) {
    case 'xs':
      return css(["height:", ";", ""], deriveSize(1.57), gte('desktop')(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n            height: ", ";\n          "])), deriveSize(1.625)));

    case 'sm':
      return css(["height:", ";"], deriveSize(2.5));

    case 'md':
      return css(["height:", ";"], deriveSize(3));

    case 'lg':
      return css(["height:", ";"], deriveSize(4));

    default:
      return undefined;
  }
});

var fontBySizeMixin = size => map(size, val => {
  switch (val) {
    case 'xs':
      return css(["font-size:", ";", ""], deriveSize(0.7857), gte('desktop')(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["font-size: ", ";"])), deriveSize(0.75)));

    case 'sm':
      return css(["font-size:", ";"], deriveSize(0.875));

    case 'md':
      return css(["font-size:", ";"], deriveSize(1));

    case 'lg':
      return css(["font-size:", ";"], deriveSize(1.125));

    default:
      return undefined;
  }
});

var InputBorderColor = _ref => {
  var {
    isInverse,
    isError
  } = _ref;
  var color;

  if (!isError) {
    if (!isInverse) {
      color = getColour('border', 'interaction');
    } else {
      color = getColour('background', 'page');
    }
  } else {
    color = getColour('interaction', 'error');
  }

  return color;
};

var InputBorderHoverColor = _ref2 => {
  var {
    isInverse,
    isError
  } = _ref2;
  var color;

  if (!isError) {
    if (!isInverse) {
      color = getColour('border', 'interaction', {
        state: 'hover'
      });
    } else {
      color = getColour('shade', 0);
    }
  } else {
    color = getColour('interaction', 'error');
  }

  return color;
};

var Layout = styled.div.withConfig({
  displayName: "indexstyle__Layout",
  componentId: "lui__sc-lnm15l-0"
})(["", " display:inline-flex;flex-direction:row;align-items:center;justify-content:center;box-sizing:border-box;", ""], normalise, _ref3 => {
  var {
    isFullWidth,
    size,
    isInverse,
    isDisabled
  } = _ref3;
  return isDisabled ? css(["border-radius:", ";cursor:not-allowed;width:", ";background-color:", ";border:1px solid ", ";:hover:not(:focus){border:1px solid ", ";}", " ", ""], size === 'xs' ? '12px' : getRadius(), isFullWidth ? '100%' : 'auto', isInverse ? 'transparent' : getColour('background', 'page'), isInverse ? getColour('fill', 'main') : getColour('border', 'container'), InputBorderHoverColor, heightBySizeMixin(size), isFullWidth ? css(["width:100%;"]) : widthBySizeMixin(size)) : css(["border-radius:", ";border:1px solid ", ";width:", ";background-color:", ";", " ", ""], size === 'xs' ? '21px' : getRadius(), InputBorderColor, isFullWidth ? '100%' : 'auto', isInverse ? 'transparent' : getColour('fill', 'main'), heightBySizeMixin(size), isFullWidth ? css(["width:100%;"]) : widthBySizeMixin(size));
});

var widthBySizeMixin = size => map(size, val => {
  switch (val) {
    case 'xs':
      return css(["width:", ";"], deriveSize(12.5));

    case 'sm':
      return css(["width:", ";"], deriveSize(14.5));

    case 'md':
      return css(["width:", ";"], deriveSize(21.5));

    case 'lg':
      return css(["width:", ";"], deriveSize(24));

    default:
      return css(["width:", ";"], deriveSize(21.5));
  }
});

var InputWrapper = styled.input.withConfig({
  displayName: "indexstyle__InputWrapper",
  componentId: "lui__sc-lnm15l-1"
})(["flex:1 1 auto;height:100%;min-width:0;width:100%;border:0px;&::placeholder{color:", ";}&:disabled{cursor:not-allowed;", ";}", ""], getColour('text', 'subtle'), _ref4 => {
  var {
    isInverse
  } = _ref4;
  return css(["background-color:", ";"], isInverse ? 'transparent' : getColour('fill', 'disabled'));
}, _ref5 => {
  var {
    fontSize,
    isInverse
  } = _ref5;
  return css(["", " border-radius:", ";color:", ";background-color:", ";font-family:", ";padding:", ";"], fontBySizeMixin(fontSize), fontSize === 'xs' ? '21px' : getRadius(), isInverse ? getColour('fill', 'main') : getColour('text', 'main'), isInverse ? 'transparent' : getColour('fill', 'main'), getTypography('body1', 'fontFamily'), fontSize === 'xs' ? "0 ".concat(deriveSize(0.25)) : "0 ".concat(deriveSize(1)));
});
var BeforeWrapper = styled.span.withConfig({
  displayName: "indexstyle__BeforeWrapper",
  componentId: "lui__sc-lnm15l-2"
})(["", "{border-top-right-radius:0;border-bottom-right-radius:0;margin:0 0 0 -1px;}box-sizing:content-box;padding-right:1px;flex:1 0 auto;line-height:1px;", ""], InputButton, _ref6 => {
  var {
    isDisabled
  } = _ref6;
  return isDisabled && css(["pointer-events:none;"]);
});
var AfterWrapper = styled.span.withConfig({
  displayName: "indexstyle__AfterWrapper",
  componentId: "lui__sc-lnm15l-3"
})(["", "{border-top-left-radius:0;border-bottom-left-radius:0;margin:0 -1px 0 0;}box-sizing:content-box;padding-left:1px;flex:1 0 auto;line-height:1px;", ";"], InputButton, _ref7 => {
  var {
    isDisabled
  } = _ref7;

  if (isDisabled) {
    return css(["pointer-events:none;"]);
  }

  return undefined;
});

var _excluded = ["size", "before", "after", "isError", "isInverse", "isFullWidth", "isDisabled", "isReadOnly", "isAutoFocus", "isRequired", "inputSize", "type"];
var supportedTypes = ['text', 'email', 'number', 'tel'];
var Input = /*#__PURE__*/React.forwardRef((_ref, parentRef) => {
  var {
    size = 'md',
    before,
    after,
    isError = false,
    isInverse = false,
    isFullWidth = false,
    isDisabled = false,
    isReadOnly = false,
    isAutoFocus = false,
    isRequired = false,
    inputSize,
    type = 'text'
  } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  var disabledVal = isDisabled || !!otherProps.disabled;
  var refInput = React.useRef(null);
  React.useEffect(() => {
    if (refInput.current && (isAutoFocus || otherProps.autoFocus)) {
      refInput.current.focus();
    }
  }, []);
  return /*#__PURE__*/jsxs(Layout, {
    size: size,
    isFullWidth: isFullWidth,
    isInverse: isInverse,
    isError: isError,
    isDisabled: disabledVal,
    children: [before && /*#__PURE__*/jsx(BeforeWrapper, {
      isDisabled: disabledVal,
      children: before
    }), /*#__PURE__*/jsx(InputWrapper, _objectSpread2(_objectSpread2({}, otherProps), {}, {
      ref: parentRef !== null && parentRef !== void 0 ? parentRef : refInput,
      type: supportedTypes.includes("".concat(type)) ? "".concat(type) : 'text',
      isError: isError,
      isInverse: isInverse,
      fontSize: size,
      size: inputSize,
      readOnly: isReadOnly || otherProps.readOnly // in order to keep a backward compatibility, and giving preferences to provided InputProps, otherwise fall to HTML defaults.
      ,
      disabled: disabledVal,
      required: isRequired || otherProps.required
    })), after && /*#__PURE__*/jsx(AfterWrapper, {
      isDisabled: isDisabled,
      children: after
    })]
  });
});
Input.displayName = 'TextInput';

export { Input, InputButton, Input as default, supportedTypes };
